'use strict';

import Ractive from 'ractive';
import FundingCalculator from './funding-calculator';

// Configure Ractive.js debug mode
// http://www.ractivejs.org/
Ractive.DEBUG = false;
class fundingCalculatorUi {
  constructor() {
    this.ractiveActive = false;
    this.ractiveElement = null;
  }

  teardown() {
    var fundingCalculatorUi = this;

    // Don't run teardown code if it's already done before
    if (!fundingCalculatorUi.ractiveActive) {
      return;
    }

    fundingCalculatorUi.ractiveActive = false;
    fundingCalculatorUi.ractiveElement.teardown();
  }

  /**
   * Initialises funding calculator to selected element
   *
   * @param {dom} element - Dom element to attach calculator
   */
  init(element) {
    var fundingCalculatorUi = this;

    //Get options from data-attributes
    let fundingCalculatorOptions = false;
    try {
      if (element.dataset.options) {
        fundingCalculatorOptions = JSON.parse(element.dataset.options);
      }
    } catch (e) {
      return;
    }

    // Don't init if options not available
    if (!fundingCalculatorOptions) {
      return;
    }

    // Don't init if it's already done before
    if (fundingCalculatorUi.ractiveActive) {
      return;
    }

    fundingCalculatorUi.ractiveActive = true;

    // Calculator class
    FundingCalculator.construct(
      fundingCalculatorOptions.deliveryCosts, // `deliveryCosts`
      fundingCalculatorOptions.interest, // `interest` %
      fundingCalculatorOptions.baseCost, // `initialCost`
      fundingCalculatorOptions.monthlyTrancheCost // `monthlyHandlingCost`
    );

    /**
     * Funding calculator template and initial data
     */
    fundingCalculatorUi.ractiveElement = new Ractive({
      // The `el` option can be a node, an ID, or a CSS selector.
      //el: '#funding-calculator',
      el: element,

      // We could pass in a string, but for the sake of convenience
      // we're passing the ID of the `<script>` tag, which holds the template.
      template: '#ractive-funding-calculator-template',

      // Here we're passing in some initial data
      data: {
        // If math is uncountable, numbers will be hidden
        uncountable: false,

        // Helper function for formatting eur prices
        parseCurrency: fundingCalculatorUi.toLocaleStringCurrency,

        // Helper function for formatting percentage values
        parsePercentage: fundingCalculatorUi.toLocaleStringPercentage,

        // Sopimusaikavaihtoehdot (kk)
        monthlyCategories: fundingCalculatorOptions.months,

        // Valittu sopimusaika
        activeMonthlyCategory: fundingCalculatorOptions.months[0],

        // Käsiraha (defaults to some sum but can be adjusted by customer from the UI)
        deposit: fundingCalculatorOptions.deposit ? fundingCalculatorOptions.deposit : '',

        // Suurempi viimeinen erä (€)
        // Empty string will default to `0` in calculations
        // but show as "0 €" at the UI. Using `0` here would be fine,
        // but at the UI it would show up just as "0".
        lastTranche: 0,

        // Maximum amount of last installment as percentage of total price
        maxLastInstallmentPercent: fundingCalculatorOptions.maxLastInstallment,

        // Calculator on car page will show last installment based on car age
        // Calculators on other pages have no such restrictions and can always show it
        alwaysShowLastInstallment: fundingCalculatorOptions.alwaysShowLastInstallment || false,

        // Käsittelymaksu (€/erä)
        monthlyTrancheCost: fundingCalculatorOptions.monthlyTrancheCost,

        // Perustamismaksu (€)
        baseCost: fundingCalculatorOptions.baseCost,

        // Korko (%)
        interest: fundingCalculatorOptions.interest,

        // Toimituskulut (€)
        deliveryCosts: fundingCalculatorOptions.deliveryCosts,

        // Kuukausierä (ilman käsittelymaksua)
        monthlyTranche: 0,

        price: fundingCalculatorOptions.price,

        // Rahoitettava osuus
        financedAmount: 0,

        // KSL:n mukainen luottohinta
        creditPrice: 0,

        // KSL:n mukaiset luottokustannukset
        creditCosts: 0,

        // KSL:n mukainen vuosikorko %
        // @link https://kulutusluottovertailu.fi/todellinen-vuosikorko/
        //
        // Kuluttajaluottojen todellisen vuosikoron laskentakaava on
        // Oikeusministeriön asetuksen 824/2010 liitteessä ja se
        // perustuu EU-direktiiviin:
        // http://www.finlex.fi/data/sdliite/liite/6176.pdf
        realYearlyInterest: 0,
        // Autosalpasopimus
        mileageOptions: fundingCalculatorOptions.mileageOptions,
        activeMileageOptions: fundingCalculatorOptions.mileageOptions[0],
        carName: `${fundingCalculatorOptions.manufacturer.toUpperCase()} ${fundingCalculatorOptions.model} ${fundingCalculatorOptions.version}`,
        imageLink: fundingCalculatorOptions.image,
        carId: fundingCalculatorOptions.id,
        emails: fundingCalculatorOptions.emails
      },
      computed: {
        maxLastInstallment: function() {
          //Maximum amount of last installment in currency (a percentage of total price)
          return (this.get('maxLastInstallmentPercent') * (parseInt(String(this.get('price')).replace(' ', ''), 10) || 0)) / 100;
        },
        financedAmountLT: function() {
          //LT will add their own baseCost, so remove basecost from the amount that is passed to LT
          return this.get('financedAmount') - this.get('baseCost');
        }
      }
    });

    // Update model to take into accoun't `currencyinput`'s initial format
    // (could be `1 500` while model would stay at `1500` without this)
    // http://docs.ractivejs.org/latest/lifecycle-events
    fundingCalculatorUi.ractiveElement.on('complete', function() {
      fundingCalculatorUi.ractiveElement.updateModel();
    });

    // The observer will be initialised with ( currentValue, undefined ) unless
    // we pass a third `options` argument in which `init` is `false`.
    fundingCalculatorUi.ractiveElement.observe('deposit lastTranche activeMonthlyCategory price activeMileageOptions', function(newValue, oldValue, keypath) {
      // Get values from the model
      var emails = fundingCalculatorUi.ractiveElement.get('emails');
      var deposit = fundingCalculatorUi.ractiveElement.get('deposit');
      var lastTranche = fundingCalculatorUi.ractiveElement.get('lastTranche');
      var activeMonthlyCategory = fundingCalculatorUi.ractiveElement.get('activeMonthlyCategory');
      var activeMileageOptions = fundingCalculatorUi.ractiveElement.get('activeMileageOptions');
      var price = fundingCalculatorUi.ractiveElement.get('price');
      // Ensure we have integers
      // `NaN` turns to `0`
      // `10` in parseInt is radix
      deposit = parseInt(String(deposit).replace(' ', ''), 10) || 0;
      activeMonthlyCategory = parseInt(activeMonthlyCategory, 10) || 0;
      activeMileageOptions = parseInt(activeMileageOptions, 10) || 0;
      price = parseInt(String(price).replace(' ', ''), 10) || 0;

      //Price forced to minimum 1000
      if (price < 1000) {
        price = 1000;
        fundingCalculatorUi.ractiveElement.set('price', price);
      }

      // Empty means `0`
      if (lastTranche === '') {
        fundingCalculatorUi.ractiveElement.set('lastTranche', 0);
      } else {
        //Parse amount
        lastTranche = parseInt(String(lastTranche).replace(' ', ''), 10) || 0;

        const maxValue = Math.round(fundingCalculatorUi.ractiveElement.get('maxLastInstallment'));

        if (lastTranche > maxValue) {
          lastTranche = maxValue;
          fundingCalculatorUi.ractiveElement.set('lastTranche', lastTranche);
        }
      }
      const leasingOptions = fundingCalculatorOptions.leasingOptions;
      const leasingOptionsByMonths = leasingOptions[activeMonthlyCategory];
      const leasingOption = leasingOptionsByMonths.find(element => {
        return +element.mileage === activeMileageOptions;
      });

      const residual_value = +leasingOption.residual_value || 0;
      const tyres_price = +leasingOption.tyres_price || 0;
      fundingCalculatorUi.ractiveElement.set('residualValue', residual_value);
      fundingCalculatorUi.ractiveElement.set('tyresPrice', tyres_price);
      var math = FundingCalculator.calculate({
        price: price - residual_value + tyres_price,
        deposit: deposit,
        paymentMonths: activeMonthlyCategory,
        lastPayment: lastTranche
      });
      /* Output:

        fundAmount: 88500
        loanExpenses: 7406.701953918012
        monthlyTranche: 1598.44
        deposit: 1500
        lastPayment: 0
        paymentMonths: 60
        creditPrice: 97406.70195391802
        totalLoanCost: 95906.70195391802
        yearlyInterest: 3.286283895308107
      */

      // Math could not return result, sets all the values to `—`
      if (!math) {
        fundingCalculatorUi.ractiveElement.set('uncountable', true);
        return;
      }

      fundingCalculatorUi.ractiveElement.set('uncountable', false);
      fundingCalculatorUi.ractiveElement.set('emails', emails);

      fundingCalculatorUi.ractiveElement.set('monthlyTranche', math.monthlyTranche);

      fundingCalculatorUi.ractiveElement.set('financedAmount', math.financedAmount);

      fundingCalculatorUi.ractiveElement.set('creditPrice', math.creditPrice);

      fundingCalculatorUi.ractiveElement.set('creditCosts', math.loanExpenses);

      fundingCalculatorUi.ractiveElement.set('realYearlyInterest', math.realYearlyInterest);
    });
  }

  /**
   * Parse eur numbers
   *
   * @param {Number} number - Number to return in Finnish EUR format
   *
   * @returns {String}
   */
  toLocaleStringCurrency(number) {
    number = Number(number);

    // Stop for falsy (except `0`)
    if (!number && number !== 0) {
      return '—';
    }

    return number.toLocaleString('fi-FI', {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  /**
   * Parse percentage numbers
   *
   * @param {Number} number - Number to return in Finnish % format
   *
   * @returns {String}
   */
  toLocaleStringPercentage(number) {
    number = Number(number);

    // Stop for falsy (except `0`)
    if (!number && number !== 0) {
      return '—';
    }

    // Divide. Otherwise `2.95` would print `295 %`
    number = number / 100;

    return number.toLocaleString('fi-FI', {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }
}

export default fundingCalculatorUi;
